import React from 'react';

/**
 * Estilos y configuraciones estandarizadas para los badges del módulo de Referidos.
 * Garantiza un ancho y alto uniforme para que las columnas no se desalineen
 * y agrupa los estados de manera armónica usando la paleta institucional del sistema.
 */

// Dimensiones fijas estándar ajustadas (más compactas y estilizadas)
export const BADGE_DIMENSIONS = {
  LINE_WIDTH: '98px',
  LINE_HEIGHT: '22px',
  STATUS_WIDTH: '118px',
  STATUS_HEIGHT: '22px',
  DOC_WIDTH: '86px',
  DOC_HEIGHT: '22px',
  DAYS_WIDTH: '40px',
  DAYS_HEIGHT: '20px',
};

// Base común para todos los badges fijos
const baseBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0px',
  padding: '0 4px',
  borderRadius: '5px',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  textTransform: 'none',
};

// 1. PALETA DE LÍNEAS DE NEGOCIO
const LINE_CONFIG: Record<
  string,
  { label: string; bg: string; color: string; border: string }
> = {
  buyer: {
    label: 'Comprador',
    bg: '#eff6ff',
    color: '#1d4ed8',
    border: '#bfdbfe',
  },
  tenant: {
    label: 'Inquilino',
    bg: '#f5f3ff',
    color: '#6d28d9',
    border: '#ddd6fe',
  },
  supply_sales: {
    label: 'Supply Sales',
    bg: '#fff7ed',
    color: '#c2410c',
    border: '#fed7aa',
  },
  supply_rents: {
    label: 'Supply Rents',
    bg: '#ecfeff',
    color: '#0e7490',
    border: '#a5f3fc',
  },
  full_service: {
    label: 'Full Service',
    bg: '#ecfdf5',
    color: '#047857',
    border: '#a7f3d0',
  },
};

export const renderLineBadge = (line?: string | null) => {
  const normalized = (line || '').toLowerCase().trim();
  const config = LINE_CONFIG[normalized] || {
    label: line || 'General',
    bg: '#f8fafc',
    color: '#475569',
    border: '#e2e8f0',
  };

  return (
    <span
      style={{
        ...baseBadgeStyle,
        width: BADGE_DIMENSIONS.LINE_WIDTH,
        height: BADGE_DIMENSIONS.LINE_HEIGHT,
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}
    >
      {config.label}
    </span>
  );
};

// 2. PALETA SEMÁNTICA DE ESTADOS (Armonizada por grupos de avance)
const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; color: string; border: string }
> = {
  // Grupo Éxito / Monetizado (Verde Propi #00c389)
  paid: {
    label: 'Pagado',
    bg: '#e6f9f3',
    color: '#00875e',
    border: '#99e7cf',
  },
  successful: {
    label: 'Exitoso',
    bg: '#e6f9f3',
    color: '#00875e',
    border: '#99e7cf',
  },
  applies: {
    label: 'Aplica',
    bg: '#e6f9f3',
    color: '#00875e',
    border: '#99e7cf',
  },

  // Grupo Citas en Agenda (Púrpura / Primario #5e72e4)
  visit_scheduled: {
    label: 'Visita agendada',
    bg: '#ede9fe',
    color: '#5b21b6',
    border: '#c4b5fd',
  },
  advisory_scheduled: {
    label: 'Asesoría agendada',
    bg: '#ede9fe',
    color: '#5b21b6',
    border: '#c4b5fd',
  },

  // Grupo Contacto Activo (Azul Info #11cdef / #0284c7)
  contacted: {
    label: 'Contactado',
    bg: '#e0f2fe',
    color: '#0369a1',
    border: '#bae6fd',
  },
  contact_updated: {
    label: 'Contacto act.',
    bg: '#e0f2fe',
    color: '#0369a1',
    border: '#bae6fd',
  },

  // Grupo En Espera (Ámbar Warning #fb6340 / #f59e0b)
  pending: {
    label: 'Pendiente',
    bg: '#fef3c7',
    color: '#92400e',
    border: '#fde68a',
  },

  // Grupo Dificultad Contacto (Coral Suave #fb6340)
  could_not_contact: {
    label: 'Sin contactar',
    bg: '#ffedd5',
    color: '#9a3412',
    border: '#fed7aa',
  },

  // Grupo No Concretado (Gris Slate Muted #8898aa)
  does_not_apply: {
    label: 'No aplica',
    bg: '#f1f5f9',
    color: '#475569',
    border: '#cbd5e1',
  },
  cancelled: {
    label: 'Cancelado',
    bg: '#f1f5f9',
    color: '#475569',
    border: '#cbd5e1',
  },
};

export const renderStatusBadge = (
  status?: string | null,
  line?: string | null,
  customLabel?: string,
) => {
  const normalized = (status || '').toLowerCase().trim();
  const config = STATUS_CONFIG[normalized] || {
    label: customLabel || status || '—',
    bg: '#f8fafc',
    color: '#475569',
    border: '#e2e8f0',
  };

  // Ajuste contextual para estado 'applies' según la línea
  let displayLabel = customLabel || config.label;
  if (!customLabel && normalized === 'applies') {
    const l = (line || '').toLowerCase();
    if (l === 'buyer') {
      displayLabel = 'Reserva confirmada';
    } else if (l === 'tenant') {
      displayLabel = 'Oferta aprobada';
    }
  }

  return (
    <span
      style={{
        ...baseBadgeStyle,
        width: BADGE_DIMENSIONS.STATUS_WIDTH,
        height: BADGE_DIMENSIONS.STATUS_HEIGHT,
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}
    >
      {displayLabel}
    </span>
  );
};

// 3. BADGES DE DOCUMENTOS (DUI / Banco en Dashboard Pagos)
export const renderDocAlertBadge = (
  type: 'dui' | 'bank',
  hasDoc: boolean,
  displayValue?: string | null,
) => {
  if (hasDoc && displayValue) {
    return (
      <span className="text-dark font-weight-500" style={{ fontSize: '12px' }}>
        {displayValue}
      </span>
    );
  }

  if (hasDoc && !displayValue) {
    return (
      <span className="text-muted" style={{ fontSize: '12px' }}>
        {type === 'dui' ? 'DUI Registrado' : 'Cuenta registrada'}
      </span>
    );
  }

  const label = type === 'dui' ? 'Falta DUI' : 'Sin cuenta';

  return (
    <span
      style={{
        ...baseBadgeStyle,
        width: BADGE_DIMENSIONS.DOC_WIDTH,
        height: BADGE_DIMENSIONS.DOC_HEIGHT,
        backgroundColor: '#fffbeb',
        color: '#b45309',
        border: '1px solid #fde68a',
        gap: '4px',
      }}
    >
      <i
        className="fas fa-exclamation-triangle"
        style={{ fontSize: '9px', color: '#d97706' }}
      />
      {label}
    </span>
  );
};

// 4. PÍLDORA DE DÍAS ESPERANDO (Dashboard Pagos)
export const renderDaysWaitingBadge = (
  days?: number | null,
  isPaid?: boolean,
) => {
  if (isPaid || !days || days <= 0) {
    return <span className="text-muted font-weight-500">—</span>;
  }

  // Colores escalonados según la urgencia
  let bg = '#f1f5f9';
  let color = '#475569';
  let border = '#cbd5e1';

  if (days >= 15) {
    // Alerta alta
    bg = '#fee2e2';
    color = '#b91c1c';
    border = '#fecaca';
  } else if (days >= 7) {
    // Alerta media
    bg = '#fef3c7';
    color = '#92400e';
    border = '#fde68a';
  }

  return (
    <span
      style={{
        ...baseBadgeStyle,
        width: BADGE_DIMENSIONS.DAYS_WIDTH,
        height: BADGE_DIMENSIONS.DAYS_HEIGHT,
        backgroundColor: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {days}d
    </span>
  );
};

// 5. MICRO-BADGE COMPACTO: "Cuenta Creada"
export const renderAccountCreatedBadge = () => {
  return (
    <span
      title="Cuenta de usuario registrada en plataforma"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: '#e6f9f3',
        color: '#00875e',
        border: '1px solid #99e7cf',
        height: '18px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <i className="fas fa-check" style={{ fontSize: '8px' }} />
      Cuenta
    </span>
  );
};
